import * as React from "react";
import {useEffect, useState} from "react";
import {FacetedFilters, FILTER_FIELD_LABELS, FILTER_FIELD_DEFAULTS} from "../../model/ResearchPageState.model";
import {getTranslation} from "../../utils/translationUtil";

export interface IResearchActiveFiltersProps {
    facetedFilters: FacetedFilters;
    searchText: string;
    onRemoveFilter: (field: string, value: string) => void;
    onClearAll: () => void;
}

export const ResearchActiveFilters: React.FunctionComponent<IResearchActiveFiltersProps> = (props) => {
    // We use a fast local state so clicking the clear button hides it instantly,
    // before the heavy Redux filtering kicks in.
    const [localFilters, setLocalFilters] = useState<FacetedFilters>(props.facetedFilters);
    const [localSearch, setLocalSearch] = useState<string>(props.searchText);

    // Keep local state in sync with Redux (e.g. when typing in the search bar or clicking dropdowns)
    useEffect(() => {
        setLocalFilters(props.facetedFilters);
        setLocalSearch(props.searchText);
    }, [props.facetedFilters, props.searchText]);

    const handleRemove = (field: string, value: string) => {
        // Optimistic UI update — hide the pill instantly
        setLocalFilters((prev) => {
            const next = { ...prev };
            if (next[field]) {
                const nextSet = new Set(next[field]);
                nextSet.delete(value);
                next[field] = nextSet;
            }
            return next;
        });

        // Defer heavy Redux filtering and table redraw
        setTimeout(() => props.onRemoveFilter(field, value), 10);
    };

    const handleClearAll = () => {
        // Optimistic UI update — hide all pills instantly
        setLocalFilters({});
        setLocalSearch("");

        // Defer heavy Redux filtering and table redraw
        setTimeout(() => props.onClearAll(), 10);
    };

    const pills: {field: string; value: string; label: string}[] = [];

    for (const field of Object.keys(localFilters)) {
        const values = localFilters[field];
        if (!values || values.size === 0) continue;
        const fieldLabel = getTranslation(FILTER_FIELD_LABELS[field] || field, FILTER_FIELD_DEFAULTS[field] || field);
        values.forEach((value) => {
            pills.push({field, value, label: fieldLabel});
        });
    }

    if (pills.length === 0 && !localSearch) return null;

    return (
        <div className="research-active-filters" role="status" aria-live="polite">
            {pills.map((pill) => (
                <span key={`${pill.field}-${pill.value}`} className="research-active-filters__pill">
                    <span className="research-active-filters__pill-label">{pill.label}:</span>
                    {pill.value}
                    <button
                        type="button"
                        className="research-active-filters__pill-remove"
                        onClick={() => handleRemove(pill.field, pill.value)}
                        aria-label={getTranslation("research-page.remove-filter", "Remove {label}: {value} filter", {label: pill.label, value: pill.value})}
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </span>
            ))}
            <button
                type="button"
                className="research-active-filters__clear-all"
                onClick={handleClearAll}
            >
                {getTranslation("research-page.clear-all-filters", "Clear All Filters")}
            </button>
        </div>
    );
};
